import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../../config/axiosConfig';
import { setRealtimeEvent } from "../../realtime/realtimeSlice";
import {
  addItemToList,
  getRealtimeEntityFromPayload,
  isModuleMatch,
  removeItemFromList,
  updateItemInList,
} from "../../realtime/realtimeUtils";

export const fetchAllOngoingTasks = createAsyncThunk(
  'tasks/fetchAll',
  async (_, thunkAPI) => {
    try {
      const res = await axiosInstance.get(`/projectOngoingTask/getallongoing`);
      return res.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response.data);
    }
  }
);

export const fetchTasksByPerson = createAsyncThunk(
  'tasks/fetchByPerson',
  async (person, thunkAPI) => {
    try {
      const res = await axiosInstance.get(`/projectOngoingTask/ongoing-task/${person}`);
      return res.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response.data);
    }
  }
);

export const createTasks = createAsyncThunk(
  'tasks/create',
  async (taskData, thunkAPI) => {
    try {
      const res = await axiosInstance.post(`/projectOngoingTask/ongoing-task`, taskData);
      return res.data.tasks;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response.data);
    }
  }
);

export const updateTaskByQuotationId = createAsyncThunk(
  'tasks/updateByQuotationId',
  async ({ quotationId, updatedData }, thunkAPI) => {
    try {
      const res = await axiosInstance.put(`/projectOngoingTask/update-project/${quotationId}`, updatedData);
      return res.data.data; // returning updated task
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response.data);
    }
  }
);

export const updateTask = createAsyncThunk(
  'tasks/update',
  async ({ updates }, thunkAPI) => {
    try {
      const res = await axiosInstance.put(`/projectOngoingTask/ongoing-task/update`, { updates }); // Ensure updates is wrapped in an object
      return res.data.task;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response.data);
    }
  }
);

export const deleteTask = createAsyncThunk(
  'tasks/delete',
  async (id, thunkAPI) => {
    try {
      await axiosInstance.delete(`/projectOngoingTask/ongoing-task/${id}`);
      return id;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response.data);
    }
  }
);

const projectOngoingTaskSlice = createSlice({
  name: 'projectOngoingTasks',
  initialState: {
    tasks: [],
    allOngoingTasks: [],
    personOngoingTasks: [],
    ongoingtasks:[],
    loading: false,
    error: null
  },
  reducers: {},
  extraReducers: (builder) => {
    builder

      .addCase(fetchAllOngoingTasks.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchAllOngoingTasks.fulfilled, (state, action) => {
        state.loading = false;
        state.allOngoingTasks = action.payload;
      })
      .addCase(fetchAllOngoingTasks.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(fetchTasksByPerson.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchTasksByPerson.fulfilled, (state, action) => {
        state.loading = false;
        state.personOngoingTasks = action.payload;
      })      
      .addCase(fetchTasksByPerson.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(createTasks.pending, (state) => {
        state.loading = true;
      })
      .addCase(createTasks.fulfilled, (state, action) => {
        state.loading = false;
        state.tasks.push(...action.payload);
      })
      .addCase(createTasks.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(updateTask.fulfilled, (state, action) => {
        const index = state.tasks.findIndex(t => t === action.payload);
        if (index !== -1) {
          state.tasks[index] = action.payload;
        }
      })

      .addCase(updateTaskByQuotationId.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateTaskByQuotationId.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.tasks.findIndex(task => task.quotationId === action.payload.quotationId);
        if (index !== -1) {
          state.tasks[index] = action.payload;
        }
      })
      .addCase(updateTaskByQuotationId.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      .addCase(deleteTask.fulfilled, (state, action) => {
        state.tasks = state.tasks.filter(t => t._id !== action.payload);
      })
      .addCase(setRealtimeEvent, (state, action) => {
        const { module, action: eventAction, data } = action.payload || {};
        if (
          !isModuleMatch(module, [
            "task",
            "tasks",
            "submit-task",
            "project-assigned",
            "reassigned",
            "projectaction",
          ])
        ) {
          return;
        }

        const incomingItem = getRealtimeEntityFromPayload(data);
        if (!incomingItem) return;

        if (eventAction === "updated") {
          state.tasks = updateItemInList(state.tasks, incomingItem);
          state.allOngoingTasks = updateItemInList(state.allOngoingTasks, incomingItem);
          state.personOngoingTasks = updateItemInList(state.personOngoingTasks, incomingItem);
          state.ongoingtasks = updateItemInList(state.ongoingtasks, incomingItem);
        }

        if (eventAction === "created") {
          state.tasks = addItemToList(state.tasks, incomingItem);
          state.allOngoingTasks = addItemToList(state.allOngoingTasks, incomingItem);
          state.personOngoingTasks = addItemToList(state.personOngoingTasks, incomingItem);
          state.ongoingtasks = addItemToList(state.ongoingtasks, incomingItem);
        }

        if (eventAction === "deleted") {
          state.tasks = removeItemFromList(state.tasks, incomingItem);
          state.allOngoingTasks = removeItemFromList(state.allOngoingTasks, incomingItem);
          state.personOngoingTasks = removeItemFromList(state.personOngoingTasks, incomingItem);
          state.ongoingtasks = removeItemFromList(state.ongoingtasks, incomingItem);
        }
      });
  }
});

export default projectOngoingTaskSlice.reducer;
